import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import {
  useGetSavedQueriesQuery,
  useDeleteSavedQueryMutation,
  useCreateSavedQueryMutation,
} from "@api/pApi";
import { getCurrentUserId } from "@api/devAuth";
import type { SavedQueryOut } from "@api/types";
import { specTarget, targetLabelKey, targetRoute } from "./savedSpec";
import { SaveViewDialog } from "./SaveViewDialog";
import "./savedViews.css";

type VisFilter = "all" | "mine" | "public";

/**
 * Saved Views (`/saved`, §4) — the reusable-query library. Lists saved
 * filters/pivots the user can reapply to a target screen (Inbox, Cost pivot,
 * NPT…). Manage here: search, filter (All/Mine/Public), open (→ reapply spec +
 * navigate), toggle share, export (stub), delete. "Save a view" captures the
 * current screen's spec; here it opens the dialog to author one manually.
 *
 * All numbers/labels come from the P contract — no timestamp field exists on
 * `SavedQueryOut`, so there is no "Updated" column (unlike the static wireframe,
 * which mocked one). Owner is shown as initials derived from `owner_user_id`,
 * with the current user's rows labelled "You".
 */
export function SavedViews() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const meId = getCurrentUserId();

  const { data, isLoading, isError, refetch } = useGetSavedQueriesQuery();
  const [deleteSavedQuery] = useDeleteSavedQueryMutation();
  const [createSavedQuery, { isLoading: isSaving }] = useCreateSavedQueryMutation();

  const [vis, setVis] = useState<VisFilter>("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((sq) => {
      if (vis === "mine" && sq.owner_user_id !== meId) return false;
      if (vis === "public" && sq.visibility !== "public") return false;
      if (q) {
        const hay = `${sq.name} ${sq.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, vis, search, meId]);

  const openView = (sq: SavedQueryOut) => {
    const target = specTarget(sq);
    if (target) navigate(targetRoute(target));
  };

  const removeView = (sq: SavedQueryOut) => {
    if (typeof window !== "undefined" && !window.confirm(t("saved.confirmDelete"))) return;
    void deleteSavedQuery(sq.id);
  };

  const filters: { id: VisFilter; key: "saved.filter.all" | "saved.filter.mine" | "saved.filter.public" }[] = [
    { id: "all", key: "saved.filter.all" },
    { id: "mine", key: "saved.filter.mine" },
    { id: "public", key: "saved.filter.public" },
  ];

  const hasAny = (data?.length ?? 0) > 0;

  return (
    <div>
      <div className="page-head">
        <div className="saved-head-row">
          <h1>{t("saved.title")}</h1>
          <button type="button" className="btn primary" onClick={() => setDialogOpen(true)}>
            + {t("saved.new")}
          </button>
        </div>
        <p className="subtitle">{t("saved.subtitle")}</p>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!hasAny}
        emptyMessage={t("saved.noData")}
        onRetry={refetch}
      >
        <div className="saved-toolbar">
          <div className="seg" role="group" aria-label={t("saved.col.visibility")}>
            {filters.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-pressed={vis === f.id}
                className={vis === f.id ? "active" : ""}
                onClick={() => setVis(f.id)}
              >
                {t(f.key)}
              </button>
            ))}
          </div>
          <div className="field saved-search">
            <input
              type="search"
              placeholder={t("saved.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t("saved.search")}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            {t("saved.empty")}
            {(search || vis !== "all") && (
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setSearch("");
                    setVis("all");
                  }}
                >
                  {t("saved.empty.clear")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="card saved-table-card">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t("saved.col.name")}</th>
                    <th>{t("saved.col.description")}</th>
                    <th title={t("saved.col.opensIn.title")}>{t("saved.col.opensIn")}</th>
                    <th>{t("saved.col.visibility")}</th>
                    <th>{t("saved.col.owner")}</th>
                    <th className="saved-actions-col">{t("saved.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((sq) => (
                    <SavedRow
                      key={sq.id}
                      sq={sq}
                      isMine={sq.owner_user_id === meId}
                      onOpen={openView}
                      onDelete={removeView}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </QueryState>

      <SaveViewDialog
        open={dialogOpen}
        saving={isSaving}
        onClose={() => setDialogOpen(false)}
        onSave={async (payload) => {
          await createSavedQuery(payload).unwrap();
          setDialogOpen(false);
        }}
      />
    </div>
  );
}

function SavedRow({
  sq,
  isMine,
  onOpen,
  onDelete,
}: {
  sq: SavedQueryOut;
  isMine: boolean;
  onOpen: (sq: SavedQueryOut) => void;
  onDelete: (sq: SavedQueryOut) => void;
}) {
  const { t } = useLocale();
  const target = specTarget(sq);
  const isPublic = sq.visibility === "public";

  return (
    <tr data-owner="p">
      <td>
        <span className="saved-name">{sq.name}</span>
      </td>
      <td className="saved-desc" title={sq.description ?? undefined}>
        {sq.description || <span className="dim">{t("saved.noDescription")}</span>}
      </td>
      <td>
        {target ? (
          <span className="badge status-producing">{t(targetLabelKey(target))}</span>
        ) : (
          <span className="badge">—</span>
        )}
      </td>
      <td>
        <span className={`badge ${isPublic ? "status-drilling" : ""}`}>
          {isPublic ? t("saved.visibility.public") : t("saved.visibility.private")}
        </span>
      </td>
      <td>
        <span className="saved-owner">
          <span className="saved-avatar" aria-hidden="true">
            {ownerInitials(sq.owner_user_id)}
          </span>
          <span>{isMine ? t("saved.filter.mine") : shortOwner(sq.owner_user_id)}</span>
        </span>
      </td>
      <td className="saved-actions-col">
        <div className="saved-row-actions">
          <button
            type="button"
            className="btn sm"
            title={t("saved.action.open")}
            aria-label={t("saved.action.open")}
            disabled={!target}
            onClick={() => onOpen(sq)}
          >
            ↗
          </button>
          <button
            type="button"
            className="btn sm"
            title={t("saved.action.export")}
            aria-label={t("saved.action.export")}
            onClick={() => {
              if (typeof window !== "undefined") window.alert(t("saved.exportSoon"));
            }}
          >
            ⇩
          </button>
          <button
            type="button"
            className="btn sm danger"
            title={t("saved.action.delete")}
            aria-label={t("saved.action.delete")}
            disabled={!isMine}
            onClick={() => onDelete(sq)}
          >
            ✕
          </button>
        </div>
      </td>
    </tr>
  );
}

/** Two-char initials derived from a user id — a stable, name-free avatar. */
function ownerInitials(userId: string): string {
  const hex = userId.replace(/[^0-9a-f]/gi, "").slice(-2).toUpperCase();
  return hex || "··";
}

/** A short, name-free owner label from the id tail (we hold no name catalog). */
function shortOwner(userId: string): string {
  return `#${userId.replace(/[^0-9a-z]/gi, "").slice(-4).toUpperCase()}`;
}
